import type { Account } from "@groundspark/browser-auth";
import { type CancellableMetricRequest, IntelligenceApi } from "./api";
import { AuthenticationRequiredError, type AuthManager } from "./auth";
import { brandLockupContent } from "./brand";
import { HelpTooltips } from "./help-tooltips";
import { RankedAreasPanel } from "./ranked-areas";
import { AreaAnalysis } from "./area-analysis";
import { AreaSearch } from "./area-search";
import {
  CANVASS_RESULT_FIELDS,
  canvassLegend,
  type CanvassResultField,
  type CanvassingProject,
} from "./canvassing";
import { renderConstituencyResult, type ConstituencyElectionResult } from "./elections";
import { renderCouncilResults, type CouncilAreaResults } from "./council-elections";
import type {
  AffinityGroup,
  AffinitySubgroup,
  AffinityRecipe,
  AnalysisModelCatalog,
  AnalysisModelId,
  CatalogResponse,
  ElectoralRegisterSpec,
  MapMetadataResponse,
  MetricRequest,
  MetricResponse,
  OperationalModelParams,
  StatisticSpec,
} from "./domain";
import type { HoveredMapArea, IntelligenceMap, LabelGeography } from "./map-view";
import {
  aggregateStatistic,
  areaGeographyLabel,
  formatCompletionHours,
  formatCount,
  formatDensity,
  formatMedianAge,
  formatPercentile,
  formatStatisticValue,
  projectionDescription,
  statisticDisplayName,
  statisticSourceGeography,
  statisticSourceLabel,
  validateAffinityRecipe,
  validateOperationalParameters,
} from "./presentation";

type MetricMode = "statistic" | "affinity" | "operational_priority";
interface AnalysisSnapshot {
  readonly request: MetricRequest;
  readonly metadata: MapMetadataResponse;
  readonly metric: MetricResponse;
  readonly areas: AreaAnalysis;
}

interface AnalysisModelState {
  selectedStatisticId: string;
  appliedRecipe: AffinityRecipe;
  draftRecipe: AffinityRecipe;
}
const REGISTER_STORAGE_KEY = "groundspark_intelligence_register_v1";
const REGISTER_OFF_VALUE = "";

export class IntelligenceApplication {
  private readonly api: IntelligenceApi;
  private readonly account: Account;
  private readonly helpTooltips = new HelpTooltips();
  private catalog!: CatalogResponse;
  private display!: AnalysisSnapshot;
  private selection!: MetricRequest;
  private nationalElectorCounts: readonly number[] | null = null;
  private register: ElectoralRegisterSpec | null = null;
  private registerRequest: AbortController | null = null;
  private registerError: string | null = null;
  private map: IntelligenceMap | null = null;
  private rankedAreas: RankedAreasPanel | null = null;
  private areaSearch: AreaSearch | null = null;
  private currentMetricRequest: CancellableMetricRequest | null = null;
  private destroyed = false;
  private readonly modelStates = new Map<AnalysisModelId, AnalysisModelState>();
  private operationalParameters!: OperationalModelParams;
  private draftOperationalParameters!: OperationalModelParams;
  private labelGeography: LabelGeography = "off";
  private continuousColours = true;
  private exactPercentiles = true;
  private basemapVisible = true;
  private thematicOpacity = 0.82;
  private hover: HoveredMapArea | null = null;
  private mapError: string | null = null;
  private projects: readonly CanvassingProject[] = [];
  private projectId: string | null = null;
  private canvassingResultField: CanvassResultField = "general_outcome";
  private projectRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private projectRequest: AbortController | null = null;
  private electionResults: ReadonlyMap<string, ConstituencyElectionResult> | null = null;
  private electionRequest: AbortController | null = null;
  private electionError: string | null = null;
  private constituency: HoveredMapArea | null = null;
  private councilArea: HoveredMapArea | null = null;
  private councilResults: CouncilAreaResults | null = null;
  private councilError: string | null = null;
  private councilRequest: AbortController | null = null;
  private councilTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly councilCache = new Map<string, CouncilAreaResults>();

  public constructor(
    private readonly root: HTMLElement,
    private readonly authentication: AuthManager,
    private readonly onAuthenticationRequired: (error: AuthenticationRequiredError) => void,
  ) {
    const account = authentication.account;
    if (account === null) throw new Error("Authenticated account is unavailable.");
    this.account = account;
    this.api = new IntelligenceApi(authentication);
  }

  public async start(): Promise<void> {
    this.showLoading("Loading catalogue and campaign models…");
    this.catalog = await this.api.catalog();
    for (const model of this.catalog.analysisModels) {
      this.modelStates.set(model.id, {
        selectedStatisticId: model.initialStatisticId,
        appliedRecipe: cloneRecipe(model.defaultAffinityRecipe),
        draftRecipe: cloneRecipe(model.defaultAffinityRecipe),
      });
    }
    const defaultPreset =
      this.catalog.operationalPresets.find((preset) => preset.preset === "canvassing") ??
      this.catalog.operationalPresets[0];
    if (defaultPreset === undefined) throw new Error("No operational presets are available.");
    this.operationalParameters = { ...defaultPreset.parameters };
    this.draftOperationalParameters = { ...defaultPreset.parameters };
    this.register = selectedRegister(this.catalog.electoralRegisters);

    this.showLoading(
      "Loading map metadata, demographic affinity, and permitted private layers…",
    );
    const [, , projects] = await Promise.all([
      this.loadAnalysis(this.metricRequest("england_wales", "affinity")),
      this.loadRegister(),
      this.api.canvassingProjects(),
    ]);
    this.projects = projects;
    const { IntelligenceMap } = await import("./map-view");
    if (!this.destroyed) this.renderApplication(IntelligenceMap);
  }

  public destroy(): void {
    this.destroyed = true;
    this.registerRequest?.abort();
    if (this.projectRefreshTimer !== null) clearTimeout(this.projectRefreshTimer);
    this.projectRequest?.abort();
    this.electionRequest?.abort();
    this.councilRequest?.abort();
    if (this.councilTimer !== null) clearTimeout(this.councilTimer);
    this.helpTooltips.close();
    this.currentMetricRequest?.cancel();
    this.currentMetricRequest = null;
    this.map?.destroy();
    this.rankedAreas?.destroy();
    this.areaSearch?.destroy();
  }

  private showLoading(message: string): void {
    this.root.innerHTML = `
      <main class="auth-screen" id="main-content" tabindex="-1">
        <section class="auth-card">
          <div class="brand-lockup">${brandLockupContent()}</div>
          <p class="eyebrow">Preparing national map</p>
          <div class="control-row"><span class="spinner" aria-hidden="true"></span><span data-loading-message></span></div>
        </section>
      </main>`;
    const target = this.root.querySelector<HTMLElement>("[data-loading-message]");
    if (target !== null) target.textContent = message;
  }

  private renderApplication(MapConstructor: typeof import("./map-view").IntelligenceMap): void {
    this.root.innerHTML = `
      <div class="intelligence-app">
        <header class="app-header">
          <a class="app-header__logo" href="/" aria-label="Groundspark Intelligence home">${brandLockupContent()}</a>
          <div class="options-menu" data-options-menu>
            <button class="button button--light-blue" type="button" data-action="options" aria-expanded="false" aria-haspopup="dialog" aria-controls="map-options">Options</button>
            <div class="options-menu__panel" id="map-options" role="dialog" aria-label="Map options" hidden>
              <label><input type="checkbox" data-control="ranked-areas" /> Ranked areas</label>
              <label><input type="checkbox" data-control="exact-percentiles" checked /> Percentile labels</label>
              <label><input type="checkbox" data-control="continuous-colours" checked /> Continuous colours</label>
              <label><input type="checkbox" data-control="basemap" checked /> OpenStreetMap context</label>
              <label class="options-menu__range"><span class="control-label">Thematic opacity</span><input type="range" data-control="opacity" min="45" max="95" value="82" /></label>
            </div>
          </div>
          <div class="area-search" data-area-search></div>
          <div class="account-bar"><div class="account-copy"><strong data-account-name></strong><span data-account-org></span></div><button class="button button--white" type="button" data-action="sign-out">Sign out</button></div>
        </header>
        <section class="control-deck" aria-label="Map controls">
          <div class="control-deck__toolbar">
            <label class="control-group"><span class="control-label">Labels</span><select data-control="labels"><option value="off">Off</option><option value="base_area">Base areas</option><option value="lsoa">LSOA</option><option value="msoa">MSOA</option><option value="council_ward">Council wards</option><option value="county_division">County divisions</option><option value="constituency">Constituencies</option><option value="settlement">Settlements</option></select></label>
            <div class="control-group control-deck__section">
              <span class="control-label">Metric</span>
              <span class="control-group" data-metric-selector role="group" aria-label="Map metric"><button class="button button--white" data-action="statistic" aria-pressed="false">Statistic</button><button class="button button--white" data-action="affinity" aria-pressed="true">Demographic Affinity</button><button class="button button--white" data-action="operational" aria-pressed="false">Operational Priority</button></span>
            </div>
            <section class="control-group control-deck__section" aria-label="Electoral roll and canvassing">
              <label class="control-group" data-register-control hidden><span class="control-label">Roll</span><select data-control="register"></select></label>
              <label class="control-group"><span class="control-label">Canvassing project</span><select data-control="project"><option value="">Off</option></select></label>
            </section>
          </div>
          <div class="control-row" data-metric-context hidden><span class="muted" data-statistic-context></span></div>
          <div class="control-row" data-register-status role="status" hidden><span data-register-message></span><button class="button button--white" type="button" data-action="retry-register" hidden>Retry roll</button></div>
          <div class="canvassing-legend" data-canvassing-legend aria-label="Canvassing legend" hidden></div>
          <p class="error-text" data-canvassing-error role="status" hidden></p>
        </section>
        <main class="workspace" id="main-content" tabindex="-1">
          <aside class="sidebar" data-sidebar></aside>
          <section class="map-shell"><div class="map" data-map></div><div class="map-status" data-map-error hidden></div></section>
          <div class="results-sidebar">
            <aside class="ranking-sidebar" data-ranked-areas aria-label="Ranked areas" hidden></aside>
            <aside class="election-sidebar" aria-label="Election results">
              <div class="sidebar-content election-content">
                <section class="election-group"><h2>Parliamentary</h2><div data-election-results aria-live="polite"></div></section>
                <div data-council-results aria-live="polite"></div>
              </div>
            </aside>
          </div>
        </main>
        <section class="hover-status" data-hover aria-live="polite"><p class="status-placeholder">Move over a base area or label to inspect the selected metric. Right-click to pin; right-click again to release.</p></section>
      </div>`;
    this.text("[data-account-name]", this.account.user.displayName);
    this.text("[data-account-org]", `${this.account.organization.name} · ${this.account.role}`);
    this.populateRegisterSelect();
    this.populatePointControls();
    this.populateProjectSelect();
    this.areaSearch = new AreaSearch(
      this.required<HTMLElement>("[data-area-search]"),
      this.api,
      (area) => this.map?.focusArea(area),
      (error) => {
        this.destroy();
        this.onAuthenticationRequired(error);
      },
    );
    this.updateAreaSearch();
    this.bindControls();
    this.updateStatisticContext();
    this.updateModeControls();

    this.rankedAreas = new RankedAreasPanel(
      this.required<HTMLElement>("[data-ranked-areas]"),
      this.api,
      (area) => this.map?.focusArea(area),
      (error) => {
        this.destroy();
        this.onAuthenticationRequired(error);
      },
    );

    const mapContainer = this.required<HTMLElement>("[data-map]");
    this.map = new MapConstructor(
      mapContainer,
      this.authentication,
      this.catalog,
      this.display.request.model,
      this.display.metadata,
      this.register?.id ?? null,
      this.display.metric.percentiles,
      {
        onHover: (target) => {
          this.hover = target;
          this.renderHover();
        },
        onConstituencyHover: (target) => {
          this.constituency = target;
          this.renderElectionResults();
        },
        onElectionAreaHover: (target) => this.selectCouncilArea(target),
        onError: (message) => {
          this.mapError = message;
          this.renderMapError();
        },
        onCanvassingError: (message) => this.showCanvassingError(message),
      },
    );
    this.updateRankedAreas();
    void this.loadElectionResults();
  }

  private updateRankedAreas(): void {
    if (!this.root.querySelector<HTMLInputElement>("[data-control=ranked-areas]")?.checked)
      return;
    const { request, metric, areas } = this.display;
    const geography = this.labelGeography === "off" ? "base_area" : this.labelGeography;
    const model = this.catalog.analysisModels.find(({ id }) => id === request.model);
    this.rankedAreas?.update({
      model: request.model,
      geography,
      region: model?.label ?? request.model,
      metric: metric.label,
      percentiles: areas.percentiles(geography),
    });
  }

  private updateAreaSearch(): void {
    if (this.areaSearch === null) return;
    const labels = this.required<HTMLSelectElement>("[data-control=labels]");
    this.areaSearch.update({
      model: this.display.request.model,
      geography: this.labelGeography,
      label: labels.options[labels.selectedIndex]?.textContent?.toLowerCase() ?? "areas",
    });
  }

  private async loadElectionResults(): Promise<void> {
    this.electionRequest?.abort();
    const controller = new AbortController();
    this.electionRequest = controller;
    this.electionError = null;
    this.renderElectionResults();
    try {
      const results = await this.api.parliamentResults(controller.signal);
      if (controller.signal.aborted || this.destroyed) return;
      this.electionResults = new Map(
        results.map((result) => [result.constituencyCode, result]),
      );
    } catch (error) {
      if (controller.signal.aborted || this.destroyed) return;
      if (error instanceof AuthenticationRequiredError) {
        this.destroy();
        this.onAuthenticationRequired(error);
        return;
      }
      this.electionError = error instanceof Error ? error.message : String(error);
    }
    if (!this.destroyed) this.renderElectionResults();
  }

  private renderElectionResults(): void {
    const panel = this.required<HTMLElement>("[data-election-results]");
    if (this.electionError !== null) {
      panel.innerHTML = `<p class="error-text" data-election-error></p><button class="button button--white" type="button">Retry results</button>`;
      this.text("[data-election-error]", this.electionError);
      panel.querySelector("button")?.addEventListener("click", () => {
        void this.loadElectionResults();
      });
    } else if (this.electionResults === null) {
      panel.textContent = "Loading election results…";
    } else if (this.constituency === null) {
      panel.textContent = "Hover over an area to see results.";
    } else {
      const result = this.electionResults.get(this.constituency.code);
      if (result === undefined)
        panel.textContent = `No election result available for ${this.constituency.name}.`;
      else renderConstituencyResult(panel, result);
    }
  }

  private selectCouncilArea(target: HoveredMapArea | null): void {
    this.councilRequest?.abort();
    if (this.councilTimer !== null) clearTimeout(this.councilTimer);
    this.councilArea = target;
    this.councilError = null;
    const key = target === null ? "" : `${target.geography}:${target.featureId}`;
    this.councilResults = this.councilCache.get(key) ?? null;
    this.renderCouncilElectionResults();
    if (target !== null && this.councilResults === null) {
      this.councilTimer = setTimeout(() => {
        this.councilTimer = null;
        void this.loadCouncilResults(target, key);
      }, 100);
    }
  }

  private async loadCouncilResults(target: HoveredMapArea, key: string): Promise<void> {
    const controller = new AbortController();
    this.councilRequest = controller;
    try {
      const results = await this.api.councilResults(target, controller.signal);
      if (controller.signal.aborted || this.destroyed) return;
      this.councilResults = results;
      this.councilCache.set(key, results);
      if (this.councilCache.size > 128) {
        const oldest = this.councilCache.keys().next().value;
        if (oldest !== undefined) this.councilCache.delete(oldest);
      }
    } catch (error) {
      if (controller.signal.aborted || this.destroyed) return;
      if (error instanceof AuthenticationRequiredError) {
        this.destroy();
        this.onAuthenticationRequired(error);
        return;
      }
      this.councilError = error instanceof Error ? error.message : String(error);
    }
    if (!this.destroyed) this.renderCouncilElectionResults();
  }

  private renderCouncilElectionResults(): void {
    const panel = this.required<HTMLElement>("[data-council-results]");
    if (this.councilArea === null) {
      panel.replaceChildren();
    } else if (this.councilError !== null) {
      const error = document.createElement("p");
      error.className = "error-text";
      error.textContent = this.councilError;
      const retry = document.createElement("button");
      retry.className = "button button--white";
      retry.textContent = "Retry local results";
      retry.addEventListener("click", () => this.selectCouncilArea(this.councilArea));
      panel.replaceChildren(error, retry);
    } else if (this.councilResults === null) {
      panel.textContent = `Loading local election results for ${this.councilArea.name}…`;
    } else {
      renderCouncilResults(panel, this.councilArea, this.councilResults);
    }
  }

  private populateProjectSelect(): void {
    const select = this.required<HTMLSelectElement>("[data-control=project]");
    select.replaceChildren(new Option("Off", ""));
    for (const project of this.projects) {
      select.append(
        new Option(
          `${project.name} (${project.electoralRegisterId === null ? "Streets" : "Voters"})`,
          project.id,
        ),
      );
    }
    select.value = this.projectId ?? "";
    const project = this.projects.find(({ id }) => id === this.projectId);
    const legend = this.required<HTMLElement>("[data-canvassing-legend]");
    legend.replaceChildren();
    legend.hidden = project === undefined;
    if (project === undefined) return;
    const count = element("span");
    count.textContent = `${formatCount(project.canvassedCount)} ${project.electoralRegisterId === null ? "streets" : "voters"} canvassed · refreshes every 15 seconds`;
    legend.append(count);
    if (project.electoralRegisterId !== null) {
      const label = element("label", "canvassing-legend__item");
      label.append(document.createTextNode("Colour by "));
      const fieldSelect = element("select");
      fieldSelect.setAttribute("data-control", "canvassing-result-field");
      for (const [field, title] of CANVASS_RESULT_FIELDS) {
        fieldSelect.append(new Option(title, field));
      }
      fieldSelect.value = this.canvassingResultField;
      fieldSelect.addEventListener("change", () => {
        const selected = CANVASS_RESULT_FIELDS.find(([field]) => field === fieldSelect.value);
        if (selected === undefined) return;
        this.canvassingResultField = selected[0];
        this.map?.setCanvassingResultField(this.canvassingResultField);
        this.populateProjectSelect();
      });
      label.append(fieldSelect);
      legend.append(label);
    }
    const entries =
      project.electoralRegisterId === null
        ? ([["Completed street", "--blue"]] as const)
        : canvassLegend(this.canvassingResultField).map(
            ([, label, colour]) => [label, colour] as const,
          );
    for (const [label, colour] of entries) {
      const entry = element("span", "canvassing-legend__item");
      const swatch = element("i", "canvassing-legend__swatch");
      swatch.style.backgroundColor = `var(${colour})`;
      swatch.setAttribute("aria-hidden", "true");
      entry.append(swatch, document.createTextNode(label));
      legend.append(entry);
    }
  }

  private showCanvassingError(message: string | null): void {
    const target = this.required<HTMLElement>("[data-canvassing-error]");
    target.hidden = message === null;
    target.textContent = message ?? "";
  }

  private async refreshProjects(): Promise<void> {
    if (this.projectRefreshTimer !== null) clearTimeout(this.projectRefreshTimer);
    this.projectRequest?.abort();
    const request = new AbortController();
    this.projectRequest = request;
    try {
      const projects = await this.api.canvassingProjects(request.signal);
      if (this.destroyed || this.projectRequest !== request) return;
      this.projects = projects;
      const project = projects.find(({ id }) => id === this.projectId) ?? null;
      const lostAccess = this.projectId !== null && project === null;
      this.projectId = project?.id ?? null;
      this.map?.setCanvassingResultField(this.canvassingResultField);
      this.map?.setCanvassingProject(project);
      this.populateProjectSelect();
      this.showCanvassingError(
        lostAccess ? "The selected canvassing project is no longer available." : null,
      );
    } catch (error) {
      if (this.destroyed || this.projectRequest !== request) return;
      this.map?.setCanvassingProject(null);
      if (error instanceof AuthenticationRequiredError) {
        this.destroy();
        this.onAuthenticationRequired(error);
        return;
      }
      this.showCanvassingError(`Could not refresh canvassing projects: ${errorMessage(error)}`);
    } finally {
      if (!this.destroyed && this.projectRequest === request) {
        this.projectRequest = null;
        if (this.projectId !== null) {
          this.projectRefreshTimer = setTimeout(() => {
            void this.refreshProjects();
          }, 15_000);
        }
      }
    }
  }

  private populateRegisterSelect(): void {
    const wrapper = this.required<HTMLElement>("[data-register-control]");
    const select = this.required<HTMLSelectElement>("[data-control=register]");
    if (this.catalog.electoralRegisters.length === 0) return;
    wrapper.hidden = false;
    const off = document.createElement("option");
    off.value = REGISTER_OFF_VALUE;
    off.textContent = "Off";
    off.selected = this.register === null;
    select.append(off);
    for (const register of this.catalog.electoralRegisters) {
      const option = document.createElement("option");
      option.value = register.id;
      option.textContent = register.label;
      option.selected = register.id === this.register?.id;
      select.append(option);
    }
  }

  private async loadRegister(): Promise<void> {
    this.registerRequest?.abort();
    this.registerRequest = null;
    this.registerError = null;
    this.nationalElectorCounts = null;
    this.map?.setElectoralRegister(null);
    const register = this.register;
    const controller = register === null ? null : new AbortController();
    this.registerRequest = controller;
    if (this.map !== null) {
      this.renderRegisterStatus();
      this.renderHover();
    }
    if (register === null || controller === null) return;
    try {
      const bootstrap = await this.api.electoralRegisterBootstrap(
        register.id,
        controller.signal,
      );
      if (controller.signal.aborted || this.destroyed) return;
      this.nationalElectorCounts = bootstrap.electorCountsByBaseArea;
      this.map?.setElectoralRegister(register.id);
    } catch (error) {
      if (controller.signal.aborted || this.destroyed) return;
      if (this.map === null) throw error;
      if (error instanceof AuthenticationRequiredError) {
        this.destroy();
        this.onAuthenticationRequired(error);
        return;
      }
      this.registerError = errorMessage(error);
    } finally {
      if (this.registerRequest === controller) {
        this.registerRequest = null;
        if (!this.destroyed && this.map !== null) {
          this.renderRegisterStatus();
          this.renderHover();
        }
      }
    }
  }

  private renderRegisterStatus(): void {
    const loading = this.registerRequest !== null;
    this.required("[data-register-control]").setAttribute("aria-busy", String(loading));
    this.required<HTMLElement>("[data-register-status]").hidden =
      !loading && this.registerError === null;
    const message = this.required<HTMLElement>("[data-register-message]");
    message.textContent =
      this.registerError === null
        ? "Loading roll…"
        : `Couldn’t load roll: ${this.registerError}`;
    message.classList.toggle("error-text", this.registerError !== null);
    this.required<HTMLButtonElement>("[data-action=retry-register]").hidden =
      this.registerError === null;
  }

  private populatePointControls(): void {
    const container = this.required<HTMLElement>("#map-options");
    const layers = this.catalog.pointLayers.map((layer) => ({
      id: layer.id,
      label: layer.label,
      geocoded: layer.geocodedRecordCount,
      count: layer.recordCount,
    }));
    for (const layer of layers) {
      const label = document.createElement("label");
      const input = document.createElement("input");
      input.type = "checkbox";
      input.dataset.pointLayer = layer.id;
      input.title = `${formatCount(layer.geocoded)} / ${formatCount(layer.count)} geocoded. Private names and addresses remain on the server.`;
      label.append(input, document.createTextNode(` ${layer.label}`));
      container.append(label);
    }
  }

  private bindControls(): void {
    this.required<HTMLInputElement>("[data-control=ranked-areas]").addEventListener(
      "change",
      (event) => {
        const enabled = (event.currentTarget as HTMLInputElement).checked;
        this.required(".workspace").classList.toggle("workspace--ranked", enabled);
        this.updateRankedAreas();
        this.rankedAreas?.setVisible(enabled);
      },
    );
    this.required<HTMLSelectElement>("[data-control=project]").addEventListener(
      "change",
      (event) => {
        this.projectId = (event.currentTarget as HTMLSelectElement).value || null;
        this.map?.setCanvassingProject(null);
        this.populateProjectSelect();
        void this.refreshProjects();
      },
    );
    const optionsMenu = this.required<HTMLElement>("[data-options-menu]");
    const optionsToggle = this.required<HTMLButtonElement>("[data-action=options]");
    const optionsPanel = this.required<HTMLElement>("#map-options");
    const setOptionsOpen = (open: boolean): void => {
      optionsPanel.hidden = !open;
      optionsToggle.setAttribute("aria-expanded", String(open));
    };
    optionsToggle.addEventListener("click", () =>
      setOptionsOpen(optionsPanel.hidden !== false),
    );
    this.root.addEventListener("click", (event) => {
      if (event.target instanceof Node && !optionsMenu.contains(event.target)) {
        setOptionsOpen(false);
      }
    });
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || optionsPanel.hidden) return;
      setOptionsOpen(false);
      optionsToggle.focus();
    });
    this.required("[data-action=sign-out]").addEventListener("click", () => {
      void this.authentication.signOut().finally(() => window.location.assign("/"));
    });
    this.required("[data-action=statistic]").addEventListener("click", () => {
      this.selectMetricMode("statistic");
    });
    this.required("[data-action=affinity]").addEventListener("click", () => {
      this.selectMetricMode("affinity");
    });
    this.required("[data-action=operational]").addEventListener("click", () => {
      this.selectMetricMode("operational_priority");
    });
    this.required<HTMLSelectElement>("[data-control=register]").addEventListener(
      "change",
      (event) => {
        const id = (event.currentTarget as HTMLSelectElement).value;
        this.register = this.catalog.electoralRegisters.find((roll) => roll.id === id) ?? null;
        sessionStorage.setItem(REGISTER_STORAGE_KEY, this.register?.id ?? REGISTER_OFF_VALUE);
        void this.loadRegister();
      },
    );
    this.required("[data-action=retry-register]").addEventListener("click", () => {
      void this.loadRegister();
    });
    this.required<HTMLSelectElement>("[data-control=labels]").addEventListener(
      "change",
      (event) => {
        this.labelGeography = (event.currentTarget as HTMLSelectElement)
          .value as LabelGeography;
        this.selectCouncilArea(null);
        this.map?.setLabelGeography(this.labelGeography);
        this.updateAreaSearch();
        this.updateRankedAreas();
      },
    );
    this.required<HTMLInputElement>("[data-control=exact-percentiles]").addEventListener(
      "change",
      (event) => {
        this.exactPercentiles = (event.currentTarget as HTMLInputElement).checked;
        this.map?.setExactPercentiles(this.exactPercentiles);
      },
    );
    this.required<HTMLInputElement>("[data-control=continuous-colours]").addEventListener(
      "change",
      (event) => {
        this.continuousColours = (event.currentTarget as HTMLInputElement).checked;
        this.map?.setContinuousColours(this.continuousColours);
      },
    );
    this.required<HTMLInputElement>("[data-control=basemap]").addEventListener(
      "change",
      (event) => {
        this.basemapVisible = (event.currentTarget as HTMLInputElement).checked;
        this.map?.setBasemapVisible(this.basemapVisible);
      },
    );
    this.required<HTMLInputElement>("[data-control=opacity]").addEventListener(
      "input",
      (event) => {
        this.thematicOpacity = Number((event.currentTarget as HTMLInputElement).value) / 100;
        this.map?.setThematicOpacity(this.thematicOpacity);
      },
    );
    for (const input of this.root.querySelectorAll<HTMLInputElement>("[data-point-layer]")) {
      input.addEventListener("change", () => {
        this.map?.setPointLayerVisible(input.dataset.pointLayer ?? "", input.checked);
      });
    }
  }

  private selectMetricMode(mode: MetricMode): void {
    if (this.selection.kind !== mode) {
      void this.loadAnalysis(this.metricRequest(this.selection.model, mode));
    }
  }

  private updateModeControls(): void {
    this.required("[data-action=statistic]").setAttribute(
      "aria-pressed",
      String(this.selection.kind === "statistic"),
    );
    this.required("[data-action=affinity]").setAttribute(
      "aria-pressed",
      String(this.selection.kind === "affinity"),
    );
    this.required("[data-action=operational]").setAttribute(
      "aria-pressed",
      String(this.selection.kind === "operational_priority"),
    );
    this.required("[data-sidebar]").setAttribute(
      "aria-busy",
      String(this.currentMetricRequest !== null),
    );
    this.renderSidebar();
  }

  private async loadAnalysis(request: MetricRequest): Promise<void> {
    this.currentMetricRequest?.cancel();
    this.selection = request;
    const task = this.api.startMetricRequest(request);
    this.currentMetricRequest = task;
    if (this.map !== null) this.updateModeControls();
    this.setMapNotice("Loading analysis…");
    try {
      const [metadata, metric] = await Promise.all([
        this.display?.metadata.model === request.model
          ? Promise.resolve(this.display.metadata)
          : this.api.mapMetadata(request.model, task.signal),
        task.response,
      ]);
      if (this.currentMetricRequest !== task || this.destroyed) return;
      const changedModel = this.display?.request.model !== request.model;
      this.display = {
        request,
        metadata,
        metric,
        areas: new AreaAnalysis(request.kind, metric, metadata),
      };
      const state = this.currentModelState();
      if (request.kind === "statistic") {
        state.selectedStatisticId = request.statisticId;
      } else {
        state.appliedRecipe = request.recipe;
        if (request.kind === "operational_priority")
          this.operationalParameters = request.parameters;
      }
      if (changedModel) {
        this.hover = null;
        this.map?.setModel(request.model, metadata, metric.percentiles, this.continuousColours);
        this.updateAreaSearch();
      } else {
        this.map?.setMetric(metric.percentiles, this.continuousColours);
      }
      this.updateRankedAreas();
      this.setMapNotice(null);
    } catch (error) {
      if (this.currentMetricRequest !== task || this.destroyed) return;
      task.cancel();
      if (this.display === undefined) throw error;
      this.selection = this.display.request;
      if (error instanceof AuthenticationRequiredError) {
        this.destroy();
        this.onAuthenticationRequired(error);
        return;
      }
      this.setMapNotice(errorMessage(error));
    } finally {
      if (this.currentMetricRequest === task) {
        this.currentMetricRequest = null;
        if (this.map !== null) {
          this.updateModeControls();
          this.updateStatisticContext();
          this.renderHover();
        }
      }
    }
  }

  private metricRequest(modelId: AnalysisModelId, mode: MetricMode): MetricRequest {
    const state = this.modelStates.get(modelId);
    if (state === undefined) {
      throw new Error("The requested analysis model has no UI state.");
    }
    if (mode !== "statistic") {
      const recipe =
        this.selection?.model === modelId && this.selection.kind !== "statistic"
          ? this.selection.recipe
          : state.appliedRecipe;
      return mode === "affinity"
        ? { kind: mode, model: modelId, recipe }
        : {
            kind: mode,
            model: modelId,
            recipe,
            parameters:
              this.selection?.kind === "operational_priority"
                ? this.selection.parameters
                : this.operationalParameters,
          };
    }
    return { kind: mode, model: modelId, statisticId: state.selectedStatisticId };
  }

  private updateStatisticContext(): void {
    const container = this.required<HTMLElement>("[data-metric-context]");
    const target = this.required<HTMLElement>("[data-statistic-context]");
    if (this.display.request.kind === "affinity") {
      container.hidden = true;
      target.textContent = "";
      target.title = "";
      return;
    }
    container.hidden = false;
    if (this.display.request.kind === "operational_priority") {
      target.textContent =
        "Relative useful campaign outcomes per hour · server-side operational model";
      target.title = "";
      return;
    }
    const statistic = this.statisticFor(this.display.request);
    const projection = projectionDescription(statistic);
    target.textContent =
      projection ??
      `${statisticSourceLabel(statistic)} · ${statistic.denominatorColumn === null ? "Raw base-area measure" : "% of base-area table total"}`;
    target.title = projection ?? "";
  }

  private renderSidebar(): void {
    const sidebar = this.required<HTMLElement>("[data-sidebar]");
    this.helpTooltips.close();
    sidebar.replaceChildren();
    if (this.selection.kind === "statistic") this.renderStatisticSidebar(sidebar);
    else this.renderAffinitySidebar(sidebar);
  }

  private renderStatisticSidebar(sidebar: HTMLElement): void {
    const copy = button("Copy name", () => {
      void navigator.clipboard.writeText(statisticDisplayName(this.selectedStatistic()));
    });
    const header = this.sidebarHeading("Statistics", copy);

    const content = element("div", "sidebar-content");
    const list = document.createElement("fieldset");
    list.className = "statistic-list";
    const legend = document.createElement("legend");
    legend.textContent = "Choose a statistic";
    list.append(legend);
    const selectedStatisticId = this.selectedStatisticId;
    for (const statistic of this.currentModel().statistics) {
      const selected = statistic.id === selectedStatisticId;
      const option = element("label", "statistic-option");
      if (selected) option.dataset.selected = "true";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "map-statistic";
      input.value = statistic.id;
      input.checked = selected;
      input.addEventListener("change", () => {
        if (!input.checked) return;
        void this.loadAnalysis({
          kind: "statistic",
          model: this.selection.model,
          statisticId: statistic.id,
        });
      });
      const name = element("span");
      name.textContent = statisticDisplayName(statistic);
      option.append(input, name);
      list.append(option);
    }
    content.append(list);
    sidebar.append(header, content);
  }

  private renderAffinitySidebar(sidebar: HTMLElement): void {
    const header = this.sidebarHeading(
      this.selection.kind === "operational_priority" ? "Operational model" : "Affinity recipe",
    );
    const content = element("div", "sidebar-content");
    if (this.selection.kind === "operational_priority")
      content.append(this.operationalControls());
    const recipeSection = element("section", "panel-section");
    const heading = element("h3");
    heading.textContent = "Weighted statistic groups";
    recipeSection.append(heading);
    for (const [groupIndex, group] of this.draftRecipe.groups.entries()) {
      recipeSection.append(this.recipeGroup(group, groupIndex));
    }
    const addGroup = button("+ Add group", () => {
      this.draftRecipe = {
        groups: [
          ...this.draftRecipe.groups,
          {
            name: `Group ${this.draftRecipe.groups.length + 1}`,
            weightPercent: 25,
            subgroups: [],
          },
        ],
      };
      this.renderSidebar();
    });
    recipeSection.append(addGroup);
    const validation = validateAffinityRecipe(this.draftRecipe, this.currentModel().statistics);
    if (validation !== null) {
      const error = element("p", "error-text");
      error.textContent = validation;
      recipeSection.append(error);
    }
    content.append(recipeSection);
    const actions = element("footer", "panel-actions");
    const dirty = JSON.stringify(this.appliedRecipe) !== JSON.stringify(this.draftRecipe);
    const apply = button("Apply", () => {
      if (this.selection.kind !== "statistic") {
        void this.loadAnalysis({ ...this.selection, recipe: cloneRecipe(this.draftRecipe) });
      }
    });
    apply.disabled = !dirty || validation !== null;
    const revert = button("Revert", () => {
      this.draftRecipe = cloneRecipe(this.appliedRecipe);
      this.renderSidebar();
    });
    revert.disabled = !dirty;
    const reset = button("Reset", () => {
      this.draftRecipe = cloneRecipe(this.currentModel().defaultAffinityRecipe);
      this.renderSidebar();
    });
    actions.append(apply, revert, reset);
    sidebar.append(header, content, actions);
  }

  private sidebarHeading(title: string, action?: HTMLButtonElement): HTMLElement {
    const header = element("header", "sidebar-heading");
    const row = element("div", "sidebar-title");
    const heading = element("h2");
    heading.textContent = title;
    row.append(heading);
    if (action !== undefined) row.append(action);
    header.append(row, this.analysisModelTabs());
    return header;
  }

  private recipeGroup(group: AffinityGroup, groupIndex: number): HTMLElement {
    const card = element("article", "recipe-group");
    const row = element("div", "recipe-group-header");
    const name = document.createElement("input");
    name.type = "text";
    name.value = group.name;
    name.setAttribute("aria-label", "Affinity group name");
    name.addEventListener("change", () =>
      this.updateGroup(groupIndex, { ...group, name: name.value }),
    );
    row.append(
      name,
      this.helpTooltips.create(
        "About groups",
        "Positive weights favour higher values; negative weights favour lower values. Weight magnitudes are normalised to 100%; a group with weight 0 is ignored.",
      ),
      removeButton(`Remove group ${group.name}`, () => this.removeGroup(groupIndex)),
    );
    const weight = field("Group weight (%)", "number", String(group.weightPercent));
    weight.input.min = "-100";
    weight.input.max = "100";
    weight.input.step = "1";
    weight.input.addEventListener("change", () => {
      this.updateGroup(groupIndex, { ...group, weightPercent: Number(weight.input.value) });
    });
    const percentageStatistics = this.currentModel().statistics.filter(
      (statistic) => statistic.denominatorColumn !== null,
    );
    const subgroups = element("ul", "subgroup-list");
    for (const [subgroupIndex, subgroup] of group.subgroups.entries()) {
      const item = element("li", "recipe-subgroup");
      const header = element("div", "subgroup-header");
      const enabled = document.createElement("input");
      enabled.type = "checkbox";
      enabled.id = `affinity-subgroup-${groupIndex}-${subgroupIndex}`;
      enabled.checked = subgroup.enabled;
      enabled.addEventListener("change", () => {
        this.updateSubgroup(groupIndex, subgroupIndex, {
          ...subgroup,
          enabled: enabled.checked,
        });
      });
      const body = element("div", "subgroup-body");
      const text = element("label");
      text.htmlFor = enabled.id;
      text.textContent = `Subgroup ${subgroupIndex + 1}`;
      header.append(
        enabled,
        text,
        this.helpTooltips.create(
          "About subgroups",
          "Enabled subgroups have equal weight within each group. A subgroup contains one or more compatible statistics, added together.",
        ),
        removeButton(`Remove subgroup ${subgroupIndex + 1} from ${group.name}`, () =>
          this.removeSubgroup(groupIndex, subgroupIndex),
        ),
      );
      const members = element("ul", "subgroup-statistics");
      for (const [memberIndex, id] of subgroup.statisticIds.entries()) {
        const member = element("li", "subgroup-statistic");
        const copy = element("span");
        copy.textContent = statisticName(id, this.currentModel().statistics);
        member.append(
          copy,
          removeButton(
            `Remove ${copy.textContent} from subgroup ${subgroupIndex + 1} in ${group.name}`,
            () => {
              this.updateSubgroup(groupIndex, subgroupIndex, {
                ...subgroup,
                statisticIds: subgroup.statisticIds.filter((_, index) => index !== memberIndex),
              });
            },
          ),
        );
        members.append(member);
      }
      const first = this.currentModel().statistics.find(
        (statistic) => statistic.id === subgroup.statisticIds[0],
      );
      const compatible = percentageStatistics.filter(
        (statistic) =>
          !subgroup.statisticIds.includes(statistic.id) &&
          (first === undefined ||
            (statistic.file === first.file &&
              statistic.denominatorColumn === first.denominatorColumn)),
      );
      const memberPicker = document.createElement("select");
      memberPicker.setAttribute("aria-label", "Add statistic to subgroup");
      memberPicker.append(new Option("Add compatible statistic…", ""));
      for (const statistic of compatible) {
        memberPicker.append(new Option(statisticDisplayName(statistic), statistic.id));
      }
      memberPicker.addEventListener("change", () => {
        if (memberPicker.value.length === 0) return;
        this.updateSubgroup(groupIndex, subgroupIndex, {
          ...subgroup,
          statisticIds: [...subgroup.statisticIds, memberPicker.value],
        });
      });
      body.append(members, memberPicker);
      item.append(header, body);
      subgroups.append(item);
    }
    const addSubgroup = button("+ Add subgroup", () => {
      this.addSubgroup(groupIndex, {
        enabled: true,
        statisticIds: [],
      });
    });
    card.append(row, weight.label, subgroups, addSubgroup);
    return card;
  }

  private operationalControls(): HTMLElement {
    const section = element("section", "panel-section");
    const heading = element("h3");
    heading.textContent = "Activity preset";
    heading.id = "activity-preset-label";
    const preset = document.createElement("select");
    preset.setAttribute("aria-labelledby", heading.id);
    if (this.presetFor(this.draftOperationalParameters) === "custom") {
      preset.append(new Option("Custom", "custom", false, true));
    }
    for (const definition of this.catalog.operationalPresets) {
      preset.append(
        new Option(
          definition.label,
          definition.preset,
          false,
          definition.preset === this.presetFor(this.draftOperationalParameters),
        ),
      );
    }
    preset.addEventListener("change", () => {
      const definition = this.catalog.operationalPresets.find(
        (candidate) => candidate.preset === preset.value,
      );
      if (definition === undefined) return;
      this.draftOperationalParameters = { ...definition.parameters };
      if (this.selection.kind === "operational_priority") {
        void this.loadAnalysis({
          ...this.selection,
          parameters: this.draftOperationalParameters,
        });
      }
    });
    section.append(heading, preset);
    const parameters: Array<[keyof OperationalModelParams, string, number]> = [
      ["serviceSecondsPerHousehold", "Doorstep handling (seconds/household)", 1],
      ["fixedSetupSecondsPerBaseArea", "Fixed setup (seconds/base area)", 30],
      ["walkingSecondsAtReferenceDensity", "Walking at reference density", 0.5],
      ["referenceDensityHouseholdsPerKm2", "Reference density (households/km²)", 10],
      ["affinityContrastP90P10", "Affinity contrast (P90:P10)", 0.1],
      ["minimumEffectiveDensityHouseholdsPerKm2", "Minimum effective density", 1],
      ["maximumEffectiveDensityHouseholdsPerKm2", "Maximum effective density", 100],
    ];
    for (const [key, label, step] of parameters) {
      const control = field(label, "number", String(this.draftOperationalParameters[key]));
      control.input.step = String(step);
      control.input.addEventListener("change", () => {
        this.draftOperationalParameters = {
          ...this.draftOperationalParameters,
          [key]: Number(control.input.value),
        };
        this.renderSidebar();
      });
      section.append(control.label);
    }
    const validation = validateOperationalParameters(this.draftOperationalParameters);
    if (validation !== null) {
      const error = element("p", "error-text");
      error.textContent = validation;
      section.append(error);
    }
    const dirty =
      JSON.stringify(this.operationalParameters) !==
      JSON.stringify(this.draftOperationalParameters);
    const actions = element("div", "panel-actions");
    const apply = button("Apply parameters", () => {
      if (this.selection.kind === "operational_priority") {
        void this.loadAnalysis({
          ...this.selection,
          parameters: this.draftOperationalParameters,
        });
      }
    });
    apply.disabled = !dirty || validation !== null;
    const revert = button("Revert parameters", () => {
      this.draftOperationalParameters = { ...this.operationalParameters };
      this.renderSidebar();
    });
    revert.disabled = !dirty;
    actions.append(apply, revert);
    section.append(actions);
    return section;
  }

  private renderHover(): void {
    const container = this.required<HTMLElement>("[data-hover]");
    container.replaceChildren();
    if (this.hover === null) {
      const placeholder = element("p", "status-placeholder");
      placeholder.textContent =
        "Move over a base area or label to inspect the selected metric. Right-click to pin; right-click again to release.";
      container.append(placeholder);
      return;
    }
    const { request, metadata, metric, areas } = this.display;
    const indices = areas.indices(this.hover.geography, this.hover.index);
    const percentile = areas.percentile(this.hover.geography, this.hover.index);
    container.append(
      statusField(
        areaGeographyLabel(this.hover.geography),
        `${this.hover.name} · ${this.hover.code}`,
      ),
    );
    if (request.kind === "statistic") {
      const statistic = this.statisticFor(request);
      // IntelligenceApi requires aggregation inputs for every statistic response.
      const { numerators, denominators } = metric.aggregationInputs!;
      const aggregate = aggregateStatistic(indices, numerators, denominators);
      container.append(
        statusField(
          statisticDisplayName(statistic),
          formatStatisticValue(aggregate.value, statistic.denominatorColumn !== null),
        ),
      );
      if (indices.length === 1) {
        const index = indices[0];
        if (index === undefined) return;
        container.append(statusField("Base-area percentile", formatPercentile(percentile)));
        const context = metric.sourceContexts?.[index];
        if (context !== null && context !== undefined) {
          container.append(
            statusField(
              statisticSourceGeography(statistic),
              `${context.sourceAreaName} (${context.sourceAreaCode}) · ${context.sourceElectionDate}`,
            ),
          );
        }
      }
    } else {
      const households = sumAvailable(indices, areas.householdCounts);
      const area = sumArea(indices, metadata);
      const density = households === null || area <= 0 ? null : households / area;
      container.append(statusField(`${metric.label} percentile`, formatPercentile(percentile)));
      if (request.kind === "operational_priority") {
        const completion = sumAvailable(indices, metric.completionHours ?? []);
        container.append(statusField("Completion time", formatCompletionHours(completion)));
      }
      container.append(statusField("Households", formatCount(households)));
      container.append(statusField("Household density", formatDensity(density)));
    }
    const age = statusField(
      "Median age (est.)",
      formatMedianAge(
        areas.medianAge(this.hover.geography, this.hover.index),
        metadata.medianAges.topCodedAge,
      ),
    );
    age.title = `All residents at Census ${metadata.medianAges.censusYear}. Half are younger, half older. Estimated within ${metadata.medianAges.bandWidthYears}-year age bands; lower ages indicate a younger population.`;
    container.append(age);
    if (this.nationalElectorCounts !== null) {
      const mapIndices = indices.flatMap((index) => metadata.baseAreaMapIndices[index] ?? []);
      const voters = sumAvailable(mapIndices, this.nationalElectorCounts);
      const area = sumArea(indices, metadata);
      container.append(statusField("Voters", formatCount(voters)));
      container.append(
        statusField(
          "Voter density",
          formatDensity(voters === null || area <= 0 ? null : voters / area),
        ),
      );
    }
  }

  private updateGroup(index: number, group: AffinityGroup): void {
    this.draftRecipe = {
      groups: this.draftRecipe.groups.map((current, candidate) =>
        candidate === index ? group : current,
      ),
    };
    this.renderSidebar();
  }

  private removeGroup(index: number): void {
    this.draftRecipe = {
      groups: this.draftRecipe.groups.filter((_, candidate) => candidate !== index),
    };
    this.renderSidebar();
  }

  private addSubgroup(groupIndex: number, subgroup: AffinitySubgroup): void {
    const group = this.draftRecipe.groups[groupIndex];
    if (group === undefined) return;
    this.updateGroup(groupIndex, { ...group, subgroups: [...group.subgroups, subgroup] });
  }

  private updateSubgroup(
    groupIndex: number,
    subgroupIndex: number,
    subgroup: AffinitySubgroup,
  ): void {
    const group = this.draftRecipe.groups[groupIndex];
    if (group === undefined) return;
    this.updateGroup(groupIndex, {
      ...group,
      subgroups: group.subgroups.map((current, index) =>
        index === subgroupIndex ? subgroup : current,
      ),
    });
  }

  private removeSubgroup(groupIndex: number, subgroupIndex: number): void {
    const group = this.draftRecipe.groups[groupIndex];
    if (group === undefined) return;
    this.updateGroup(groupIndex, {
      ...group,
      subgroups: group.subgroups.filter((_, index) => index !== subgroupIndex),
    });
  }

  private renderMapError(): void {
    this.setMapNotice(this.mapError);
  }

  private setMapNotice(message: string | null): void {
    const target = this.root.querySelector<HTMLElement>("[data-map-error]");
    if (target === null) return;
    target.hidden = message === null;
    target.textContent = message ?? "";
  }

  private required<T extends Element = HTMLElement>(selector: string): T {
    const value = this.root.querySelector<T>(selector);
    if (value === null) throw new Error(`Application element ${selector} is missing.`);
    return value;
  }

  private text(selector: string, value: string): void {
    this.required(selector).textContent = value;
  }

  private currentModel(): AnalysisModelCatalog {
    const model = this.catalog.analysisModels.find(
      (candidate) => candidate.id === this.selection.model,
    );
    if (model === undefined) throw new Error("The selected analysis model is unavailable.");
    return model;
  }

  private currentModelState(): AnalysisModelState {
    const state = this.modelStates.get(this.selection.model);
    if (state === undefined) throw new Error("The selected analysis model has no UI state.");
    return state;
  }

  private get selectedStatisticId(): string {
    const selection = this.selection;
    return selection.kind === "statistic"
      ? selection.statisticId
      : this.currentModelState().selectedStatisticId;
  }

  private get appliedRecipe(): AffinityRecipe {
    return this.currentModelState().appliedRecipe;
  }

  private presetFor(parameters: OperationalModelParams): string {
    return (
      this.catalog.operationalPresets.find(
        (preset) => JSON.stringify(preset.parameters) === JSON.stringify(parameters),
      )?.preset ?? "custom"
    );
  }

  private get draftRecipe(): AffinityRecipe {
    return this.currentModelState().draftRecipe;
  }

  private set draftRecipe(value: AffinityRecipe) {
    this.currentModelState().draftRecipe = value;
  }

  private analysisModelTabs(): HTMLElement {
    const tabs = element("nav", "analysis-model-tabs");
    tabs.setAttribute("role", "tablist");
    tabs.setAttribute("aria-label", "Census model");
    for (const model of this.catalog.analysisModels) {
      const tab = button(model.label, () => {
        if (model.id !== this.selection.model) {
          void this.loadAnalysis(this.metricRequest(model.id, this.selection.kind));
        }
      });
      tab.dataset.analysisModel = model.id;
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", String(model.id === this.selection.model));
      tabs.append(tab);
    }
    return tabs;
  }

  private statisticFor(request: Extract<MetricRequest, { kind: "statistic" }>): StatisticSpec {
    const statistic = this.catalog.analysisModels
      .find(({ id }) => id === request.model)
      ?.statistics.find(({ id }) => id === request.statisticId);
    if (statistic === undefined) throw new Error("The displayed statistic is unavailable.");
    return statistic;
  }

  private selectedStatistic(): StatisticSpec {
    const selectedStatisticId = this.selectedStatisticId;
    const statistic = this.currentModel().statistics.find(
      ({ id }) => id === selectedStatisticId,
    );
    if (statistic === undefined) throw new Error("The selected statistic is unavailable.");
    return statistic;
  }
}

function selectedRegister(
  registers: readonly ElectoralRegisterSpec[],
): ElectoralRegisterSpec | null {
  const stored = sessionStorage.getItem(REGISTER_STORAGE_KEY);
  if (stored === REGISTER_OFF_VALUE) return null;
  return registers.find((register) => register.id === stored) ?? registers[0] ?? null;
}

function cloneRecipe(recipe: AffinityRecipe): AffinityRecipe {
  return structuredClone(recipe);
}

function statisticName(id: string, statistics: readonly StatisticSpec[]): string {
  const statistic = statistics.find((candidate) => candidate.id === id);
  return statistic === undefined ? id : statisticDisplayName(statistic);
}

function statusField(label: string, value: string): HTMLElement {
  const field = element("div", "status-field");
  const strong = element("strong");
  const copy = element("span");
  strong.textContent = label;
  copy.textContent = value;
  field.append(strong, copy);
  return field;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const value = document.createElement(tag);
  if (className !== undefined) value.className = className;
  return value;
}

function button(label: string, action: () => void, title?: string): HTMLButtonElement {
  const value = document.createElement("button");
  value.type = "button";
  value.className = "button button--white";
  value.textContent = label;
  if (title !== undefined) value.title = title;
  value.addEventListener("click", action);
  return value;
}

function removeButton(label: string, action: () => void): HTMLButtonElement {
  const value = button("×", action, label);
  value.classList.replace("button--white", "button--danger");
  value.classList.add("recipe-remove");
  value.setAttribute("aria-label", label);
  return value;
}

function field(
  labelText: string,
  type: "text" | "number",
  value: string,
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = element("label", "field");
  const copy = element("span");
  copy.textContent = labelText;
  const input = document.createElement("input");
  input.type = type;
  input.value = value;
  label.append(copy, input);
  return { label, input };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function sumAvailable(
  indices: readonly number[],
  values: readonly (number | null)[],
): number | null {
  let sum = 0;
  let available = 0;
  for (const index of indices) {
    const value = values[index];
    if (value !== null && value !== undefined && Number.isFinite(value)) {
      sum += value;
      available += 1;
    }
  }
  return available === 0 ? null : sum;
}

function sumArea(indices: readonly number[], metadata: MapMetadataResponse): number {
  return indices.reduce((sum, index) => sum + (metadata.baseAreaKm2[index] ?? 0), 0);
}
